import { HTMLAttributes, useCallback, useMemo, useRef } from 'react'
import { useEvent, useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'
import { forEach } from 'lodash'
import { RenderPass } from 'three-stdlib'

import Container from '@/components/base/Container'
import { useStore } from '@/contexts/store'
import { useLoader } from '@/store/loader'
import { isLgScreen } from '@/utils/screen'
import { type ComposerContext } from '@/webgl/contexts/composer'

import { Carousel, CarouselProps, Content, Landing } from './modules'
import { cn } from '@/lib/utils'

export type HeroSectionProps = HTMLAttributes<HTMLElement> & {
  uuid?: string
  composer?: ComposerContext
  carouselVariant?: CarouselProps['variant']
}

export type SectionHandler = {
  // eslint-disable-next-line no-unused-vars
  createTimeline: (vars?: GSAPTimelineVars) => GSAPTimeline
  // eslint-disable-next-line no-unused-vars
  createEnterTimeline: (vars?: GSAPTimelineVars) => GSAPTimeline
  // eslint-disable-next-line no-unused-vars
  createLeaveTimeline: (vars?: GSAPTimelineVars) => GSAPTimeline
  resetTimeline: () => void
}

const HeroSection = ({
  uuid = 'hero',
  carouselVariant = 'vertical',
  composer,
  ...props
}: HeroSectionProps) => {
  const { home } = useStore()
  const collection = useMemo(() => home.acf[`${uuid}_section`], [uuid, home])
  const { section_1, section_2, section_3 } = collection
  const compiled = useLoader((state) => state.compiled)
  const sceneComposer = useMemo(() => composer?.sceneComposers.get(uuid), [uuid, composer])
  const timeline = useRef<GSAPTimeline>(gsap.timeline({ paused: true, defaults: { ease: 'none' } }))
  const containers = useRef<HTMLDivElement[]>([])
  const sections = useRef<SectionHandler[]>([])
  const views = useRef<HTMLDivElement[]>([])
  const root = useRef<HTMLDivElement>(null!)

  const resetTimeline = useCallback(() => {
    timeline.current.seek(0, false)
    timeline.current.kill()
    timeline.current.clear()

    forEach(sections.current, (section) => section.resetTimeline())
    forEach(views.current, (view) => gsap.set(view, { clearProps: 'x,y' }))
  }, [])

  const updateTimeline = useCallback(() => {
    // const vw = window.innerWidth
    const vh = window.innerHeight

    timeline.current = gsap.timeline({ paused: true, defaults: { ease: 'none' } })

    /**
     * landing
     */
    const landingView = views.current[0]
    const landingSection = sections.current[0]
    const landingEnterTimeline = landingSection.createEnterTimeline({
      defaults: { immediateRender: false }
    })
    const landingLeaveTimeline = landingSection.createLeaveTimeline()

    timeline.current
      .fromTo(landingView, { y: vh }, { y: -vh, duration: vh * 2 }, 0)
      .fromTo(landingEnterTimeline, { progress: 0 }, { progress: 1, duration: vh * 0.5 }, vh * 0.5)
      .fromTo(landingLeaveTimeline, { progress: 0 }, { progress: 1, duration: vh * 1 }, vh * 0.9)

    /**
     * content
     */
    const contentView = views.current[1]
    const contentSection = sections.current[1]
    const contentContainer = containers.current[1]
    const contentEnterTimeline = contentSection.createEnterTimeline()
    const contentLeaveTimeline = contentSection.createLeaveTimeline()

    const ch = Math.max(contentView.offsetHeight, contentContainer.offsetHeight + 10)

    timeline.current
      .fromTo(contentView, { y: vh }, { y: -ch, duration: vh * 2 }, vh * 1)
      .fromTo(contentEnterTimeline, { progress: 0 }, { progress: 1, duration: vh }, vh * 1.25)
      .fromTo(contentLeaveTimeline, { progress: 0 }, { progress: 1, duration: vh }, vh * 1.95)

    /**
     * carousel
     */
    const carouselView = views.current[2]
    const carouselSection = sections.current[2]
    const carouselEnterTimeline = carouselSection.createEnterTimeline()
    const carouselLeaveTimeline = carouselSection.createLeaveTimeline()

    timeline.current.fromTo(carouselView, { y: vh }, { y: -vh, duration: vh * 2 }, vh * 2)
    timeline.current.fromTo(
      carouselEnterTimeline,
      { progress: 0 },
      { progress: 1, duration: vh * 0.75 },
      vh * 2.5
    )
    timeline.current.fromTo(
      carouselLeaveTimeline,
      { progress: 0 },
      { progress: 1, duration: vh * 0.75 },
      vh * 2.95
    )

    /**
     * sticky
     */
    const stickyView = views.current[3]
    const stickySection = sections.current[3]

    if (isLgScreen()) {
      const stickyEnterTimeline = stickySection.createEnterTimeline()
      const stickyLeaveTimeline = stickySection.createLeaveTimeline()

      timeline.current
        .fromTo(stickyView, { y: vh }, { y: 0, duration: vh * 1 }, vh * 0.5)
        .fromTo(
          stickyView,
          { y: 0 },
          { y: -vh, duration: vh * 0.8, immediateRender: false },
          vh * 3.2
        )

      timeline.current
        .fromTo(stickyEnterTimeline, { progress: 0 }, { progress: 1, duration: vh * 1 }, vh * 1.5)
        .fromTo(stickyLeaveTimeline, { progress: 0 }, { progress: 1, duration: vh * 1 }, vh * 2.9)
    }
  }, [])

  const tick = useCallback(() => {
    const renderPass = sceneComposer?.passes[0] as RenderPass
    const progress = renderPass.scene.userData.progress
    timeline.current.progress(progress)
  }, [sceneComposer])

  const update = useCallback(() => {
    resetTimeline()
    updateTimeline()
  }, [resetTimeline, updateTimeline])

  useIsomorphicLayoutEffect(() => {
    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
    }
  }, [composer, tick])

  useIsomorphicLayoutEffect(() => update(), [update])

  useEvent('resize', update)

  return (
    <>
      <section ref={root} className={cn(['static', { 'opacity-0': !compiled }])} {...props}>
        <div
          ref={(el: HTMLDivElement) => (views.current[0] = el as any)}
          className="absolute left-0 top-0 flex h-full w-full"
        >
          <Container
            ref={(el: HTMLDivElement) => (containers.current[0] = el as any)}
            className="mb-[11.1rem] mt-auto lg:mb-[13.6rem]"
          >
            <Landing
              ref={(el: SectionHandler) => (sections.current[0] = el as any)}
              overline={section_1.overline}
              title={section_1.title}
              variant="large"
            />
          </Container>
        </div>

        <div
          ref={(el: HTMLDivElement) => (views.current[3] = el as any)}
          className="absolute left-0 top-0 hidden h-full w-full lg:flex"
        >
          <Container
            ref={(el: HTMLDivElement) => (containers.current[3] = el as any)}
            className="mb-[13.6rem] mt-auto"
          >
            <Landing
              ref={(el: SectionHandler) => (sections.current[3] = el as any)}
              overline={section_1.overline}
              title={section_1.title}
              variant="small"
            />
          </Container>
        </div>

        <div
          ref={(el: HTMLDivElement) => (views.current[1] = el as any)}
          className="absolute left-0 top-0 flex h-full w-full"
        >
          <Container
            ref={(el: HTMLDivElement) => (containers.current[1] = el as any)}
            className="my-auto"
          >
            <Content
              ref={(el: SectionHandler) => (sections.current[1] = el as any)}
              title={section_2.title}
              paragraph={section_2.paragraph}
              button={section_2.secondary_button}
              variant={uuid}
            />
          </Container>
        </div>

        <div
          ref={(el: HTMLDivElement) => (views.current[2] = el as any)}
          className="absolute left-0 top-0 flex h-full w-full"
        >
          <Container
            ref={(el: HTMLDivElement) => (containers.current[2] = el as any)}
            className="my-auto"
          >
            <Carousel
              ref={(el: SectionHandler) => (sections.current[2] = el as any)}
              slides={section_3.slides}
              button={section_3.button}
              variant={carouselVariant}
            />
          </Container>
        </div>
      </section>
    </>
  )
}

export default HeroSection
